// Configuration for HPG client identification based on environment and client
export interface HPGClientConfig {
  env: string;
  clientIds: string[];
}

// Configuration for which business keys should show HPG logos by environment
export const HPG_ENABLED_MODULES: { [env: string]: { [businessKey: string]: boolean } } = {
  dev: {
    'LNK-CLINICAL-4786': true,  // Hospital Quality
    'LNK-ENTERPRI-4759': false, // Physician Review
    'LNK-HOSPITAL-5012': true,  // Hospital Utilization
    'LNK-SPOTLIGH-5056': true,  // Spotlight
    'LNK-ADVANCED-5235': true,  // Advanced Reporting
    'LNK-SURGICAL-5844': true,  // Surgical Cost
    'LNK-SURGICAL-7039': false, // Surgical Capacity
    'LNK-USERMANA-5957': false, // Settings
    'LNK-AMBULATO-7452': false,  // Ambulatory Quality
  },
  integration: {
    'LNK-HOSPITAL-5059': true,  // Hospital Quality
    'LNK-PHYSICIA-5058': false, // Physician Review
    'LNK-HOSPITAL-5060': true,  // Hospital Utilization
    'LNK-SPOTLIGH-5057': true,  // Spotlight
    'LNK-ADVANCED-5236': true,  // Advanced Reporting
    'LNK-SURGICAL-5845': true,  // Surgical Cost
    'LNK-USERMANA-5950': false, // Settings
    'LNK-SURGICAL-7038': false, // Surgical Capacity,
    'LNK-AMBULATO-7453': false,  // Ambulatory Quality
  },
  stage: {
    'LNK-HOSPITAL-565': true,   // Hospital Quality
    'LNK-HOSPITAL-562': true,   // Hospital Utilization
    'LNK-PHYSICIA-568': false,  // Physician Review
    'LNK-SPOTLIGH-559': true,   // Spotlight
    'LNK-ADVANCED-580': true,   // Advanced Reporting
    'LNK-SURGICAL-732': true,   // Surgical Cost
    'LNK-USERMANA-644': false,  // Settings
    'LNK-SURGICAL-738': false,  // Surgical Capacity
  },
  'stage-previous-version': {
    'LNK-HOSPITAL-565': true,   // Hospital Quality
    'LNK-HOSPITAL-562': true,   // Hospital Utilization
    'LNK-PHYSICIA-568': false,  // Physician Review
    'LNK-SPOTLIGH-559': true,   // Spotlight
    'LNK-ADVANCED-580': true,   // Advanced Reporting
    'LNK-USERMANA-644': false,  // Settings
    'LNK-SURGICAL-738': false,  // Surgical Capacity
  },
  uat: {
    'LNK-HOSPITAL-566': true,   // Hospital Quality
    'LNK-HOSPITAL-563': true,   // Hospital Utilization
    'LNK-PHYSICIA-569': false,  // Physician Review
    'LNK-SPOTLIGH-560': true,   // Spotlight
    'LNK-ADVANCED-581': true,   // Advanced Reporting
    'LNK-SURGICAL-731': true,   // Surgical Cost
    'LNK-USERMANA-645': false,  // Settings
    'LNK-SURGICAL-737': false,  // Surgical Capacity
  },
  production: {
    'LNK-HOSPITAL-567': true,   // Hospital Quality
    'LNK-HOSPITAL-564': true,   // Hospital Utilization
    'LNK-PHYSICIA-570': false,  // Physician Review
    'LNK-SPOTLIGH-561': true,   // Spotlight
    'LNK-ADVANCED-579': true,   // Advanced Reporting
    'LNK-SURGICAL-730': true,   // Surgical Cost
    'LNK-USERMANA-646': false,  // Settings
    'LNK-SURGICAL-734': false,  // Surgical Capacity
  }
};

// Add client IDs to the arrays when you want to enable HPG functionality for those clients.
export const HPG_ENABLED_CLIENTS: HPGClientConfig[] = [
  {
    env: 'dev',
    clientIds: ['demohpg']
  },
  {
    env: 'integration',
    clientIds: [] 
  },
  {
    env: 'stage',
    clientIds: ['cl2demo', 'demohpg', 'allifran', 'stlukehs', 'beacon']
  },
  {
    env: 'stage-previous-version',
    clientIds: [] 
  },
  {
    env: 'uat',
    clientIds: ['allifran', 'stlukehs', 'beacon'] 
  },
  {
    env: 'production',
    clientIds: ['cl2demo', 'demohpg', 'allifran', 'stlukehs', 'beacon'] 
  }
];

// Function to check if HPG logo should be displayed for a client based on their subscription key
export const shouldShowHPGLogo = (clientId: string, subscriptionKey: string): boolean => {
  const currentEnv = process.env.REACT_APP_ENV?.toLowerCase() || 'dev';
  
  // Check if client is in the configuration for current environment
  const config = HPG_ENABLED_CLIENTS.find((config: HPGClientConfig) => 
    config.env === currentEnv && config.clientIds.includes(clientId)
  );
  
  if (!config) {
    return false;
  }
  
  if (!subscriptionKey || typeof subscriptionKey !== 'string') {
    return false;
  }
  
  // Check if the subscription has HPG logo enabled
  const hpgEnabledModules = HPG_ENABLED_MODULES[currentEnv];
  const hasLogoEnabledApp = hpgEnabledModules && hpgEnabledModules[subscriptionKey] === true;
  
  return hasLogoEnabledApp;
};

// Simple function that uses permissionsList from UserContext
export const isHPGClientByPermissions = (permissionsList: any): boolean => {
  if (!permissionsList || Object.keys(permissionsList).length === 0) {
    return false;
  }

  const currentEnv = process.env.REACT_APP_ENV;
  const hpgConfig = HPG_ENABLED_CLIENTS.find(config => config.env === currentEnv);
  
  if (!hpgConfig?.clientIds.length) {
    return false;
  }

  const hpgEnabledModules = HPG_ENABLED_MODULES[currentEnv];
  if (!hpgEnabledModules) {
    return false;
  }

  // Build set of enabled module keys for O(1) lookup
  const enabledModulesSet = new Set<string>();
  for (const moduleKey in hpgEnabledModules) {
    if (hpgEnabledModules[moduleKey] === true) {
      enabledModulesSet.add(moduleKey);
    }
  }
  
  if (enabledModulesSet.size === 0) {
    return false;
  }

  // Single pass: check if any HPG client has any HPG-enabled module
  for (const clientId of hpgConfig.clientIds) {
    const client = permissionsList[clientId];
    if (client?.subscriptions) {
      // Early exit on first match
      for (const module of Object.keys(client.subscriptions)) {
        if (enabledModulesSet.has(module)) {
          return true;
        }
      }
    }
  }
  
  return false;
};

//  Determines if HPG logo should be shown based on current route and user permissions.
//  This function checks if the user is on an Advanced Reporting route and has HPG permissions.

export const shouldShowHPGLogoForRoute = (pathname: string, memberId: string | undefined): boolean => {
  // Check if current route is Advanced Reporting
  const isAdvancedReporting = pathname.startsWith('/adhoc-reports') || 
                              pathname.startsWith('/shared-reports') || 
                              pathname.startsWith('/my-reports') ||
                              pathname.startsWith('/reports');
  
  if (!isAdvancedReporting || !memberId) {
    return false;
  }

  const currentEnv = process.env.REACT_APP_ENV;
  if (!currentEnv) {
    return false;
  }

  // Get Advanced Reporting subscription key for current environment
  const hpgEnabledModules = HPG_ENABLED_MODULES[currentEnv];
  if (!hpgEnabledModules) {
    return false;
  }

  // Find the Advanced Reporting subscription key
  const advancedReportingKey = Object.keys(hpgEnabledModules).find(key => 
    key.includes('ADVANCED') && hpgEnabledModules[key] === true
  );

  if (!advancedReportingKey) {
    return false;
  }

  // Check if user has HPG permissions for Advanced Reporting
  return shouldShowHPGLogo(memberId, advancedReportingKey);
};

// Replace the domain in the URL if the current hostname indicates HPG environment
export const replaceUrlDomain = (url: string): string => {
  try {
    const urlObject = new URL(url);
    const currentHostname = window.location.hostname.toLowerCase();
    
    // Check if current hostname is HPG
    const isHPG =
      currentHostname === 'healthtrust.optum.com' ||
      currentHostname.endsWith('.healthtrust.optum.com');
    
    if (isHPG) {
      // Replace ei.optum.com with healthtrust.optum.com
      const newHostname = urlObject.hostname.toLowerCase().replace(/ei\.optum\.com$/i, 'healthtrust.optum.com');
      urlObject.hostname = newHostname;
    }
    
    return urlObject.toString();
  } catch (error) {
    // If URL parsing fails, return the original URL
    console.error('Invalid URL provided:', error);
    return url;
  }
};

// ToDo Remove this method and its references once HPG clients are onboarded
// For HPG demo purposes,
// The demo client is internally identified as 'hpg'.
export const transformMemberIdBasedOnEnvironment = (mem_id: string): string => {
  if (process.env.REACT_APP_ENV === 'dev' && mem_id === 'demohpg') {
    return 'deveng1';
  }

  if (process.env.REACT_APP_ENV === 'stage' && mem_id === 'demohpg') {
    return 'AZRXFSGHC6';
  }

  if (process.env.REACT_APP_ENV === 'production' && mem_id === 'demohpg') {
    return 'AZRXFSGHC6';
  }

  return mem_id;
};
